<template>
  <div class="sd-fravaer text-dark">
    <h2 class="marg-1-bottom capitalize">Fraværsindberetninger</h2>
    <template v-if="isNoConfiguration">
      <div class="sd-fravaer-no-config">
        <h2>
          <i>Skolens Silkeborg Data-integration er ikke konfigureret korrekt. Kontakt Docendo for at få hjælp til
            opsætningen - enten på chat eller email.</i>
        </h2>
      </div>
    </template>
    <template v-else>
      <div class="sd-fravaer__header">
        <div class="sd-fravaer__header__left">
          <Tabs
            :tabs="viewTabs"
            :selected="viewTab"
            @select="onSelectViewTab">
          </Tabs>
          <template v-if="viewTab === 'employments'">
            <DoFiltersFilter
              :title="'Personer'"
              :tags="personNames"
              :selectedTags="selectedPersonNames"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg personer'"
              @select="togglePersonName"
              @clear="selectedPersonNames = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Grupper'"
              :tags="userTags"
              :selectedTags="selectedUserTags"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg grupper'"
              @select="toggleUserTag"
              @clear="selectedUserTags = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="employmentTitles.length > 1"
              :title="'Ansættelser'"
              :tags="employmentTitles"
              :selectedTags="selectedEmploymentTitles"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg stilling'"
              @select="toggleEmploymentTitle"
              @clear="selectedEmploymentTitles = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              v-if="organisations.length > 1"
              :title="'Organisationer'"
              :tags="organisations"
              :selectedTags="selectedOrganisations"
              :numberOfSelectedTagsToShow="1"
              :placeholder="'Vælg organisation'"
              @select="toggleOrganisation"
              @clear="selectedOrganisations = []">
            </DoFiltersFilter>
            <span class="sd-fravaer__count">
              Viser {{ filteredRows.length }} af {{ allRows.length }} ansættelser
            </span>
          </template>
          <template v-else>
            <DoFiltersFilter
              :title="'Personer'"
              :tags="uniqueNames"
              :selectedTags="selectedNames"
              :numberOfSelectedTagsToShow="2"
              :placeholder="'Vælg personer'"
              @select="toggleName"
              @clear="selectedNames = []">
            </DoFiltersFilter>
            <DoFiltersFilter
              :title="'Fraværstype'"
              :tags="uniqueAbsenceTypes"
              :selectedTags="selectedAbsenceTypes"
              :numberOfSelectedTagsToShow="2"
              :placeholder="'Vælg fraværstype'"
              @select="toggleAbsenceType"
              @clear="selectedAbsenceTypes = []">
            </DoFiltersFilter>
          </template>
        </div>
        <div class="sd-fravaer__header__right" v-if="viewTab !== 'employments'">
          <button class="do-bttn do-bttn--small" :disabled="isLoading" @click="readFravaer">
            Opdater
          </button>
        </div>
      </div>

      <!-- Værktøjslinje: Alle ansættelser -->
      <div class="sd-fravaer__toolbar" v-if="viewTab === 'employments'">
        <!-- Manuel oprettelse af fravær er slået fra - fravær indberettes automatisk fra Vikardækning.
        <span class="sd-fravaer__toolbar__wrap"
              :content="selectedEmploymentIds.length === 0 ? 'Vælg personer først' : ''"
              v-tippy="{
                arrow: true,
                placement: 'top',
                animateFill: false,
                animation: 'shift-away',
                boundary: 'window'
              }">
          <button class="do-bttn do-bttn--submit"
                  :class="{'do-bttn--submit--disabled': selectedEmploymentIds.length === 0}"
                  :disabled="selectedEmploymentIds.length === 0"
                  @click="openBulkCreate">
            Opret fraværsindberetninger
          </button>
        </span>
        -->
        <DoDatePickerRange
          :allowSameDate="true"
          :startDate="autoStartDate"
          :endDate="autoEndDate"
          :maximumDateRange="365"
          @startChanged="autoStartDate = $event"
          @endChanged="autoEndDate = $event">
        </DoDatePickerRange>
        <button class="do-bttn" @click="openAutomaticIndberetning" style="font-size: 11px;">
          Indberet fravær
        </button>
      </div>

      <!-- Info: Udbakke (sendes automatisk) -->
      <div class="sd-fravaer__info" v-if="viewTab === 'outbox'">
        Fraværsindberetninger i udbakken sendes automatisk til SD senere på dagen. Du kan slette dem, så længe de ligger her.
      </div>

      <div class="sd-fravaer__body">
        <!-- Tab: Alle ansættelser -->
        <template v-if="viewTab === 'employments'">
          <Loader v-if="isLoadingEmployments"></Loader>
          <table v-else class="do-table">
            <thead>
            <tr>
              <!-- Manuel oprettelse af fravær er slået fra - fravær indberettes automatisk fra Vikardækning.
              <th class="td-check do-table__toggle-all" @click="toggleAllSelected">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isAllFilteredSelected}"></div>
              </th>
              -->
              <th>NAVN</th>
              <th>ANSÆTTELSE</th>
              <th>ORGANISATION</th>
            </tr>
            </thead>
            <tbody v-if="filteredRows.length === 0">
            <tr>
              <td :colspan="3" class="italic">Ingen ansættelser fundet</td>
            </tr>
            </tbody>
            <tbody v-for="group in groupedRows" :key="group.userId" class="user-group">
            <tr v-for="row in group.rows"
                :key="row.id"
                class="employment-row">
              <!-- Manuel oprettelse af fravær er slået fra - fravær indberettes automatisk fra Vikardækning.
              <td class="td-check" @click.stop="onCheckboxClick(row)">
                <div class="do-checkbox" :class="{'do-checkbox--selected': isRowSelected(row)}"></div>
              </td>
              -->
              <td class="bold">{{ row.userName }}</td>
              <td class="td-organisation">
                {{ row.employmentNumber }} - {{ row.employmentTitle }}
              </td>
              <td class="td-organisation">{{ row.organisation }}</td>
              <!--
              <td class="td-row-actions">
                <button
                  v-if="!isBatchMode"
                  type="button"
                  class="do-bttn do-bttn--small row-action-btn"
                  @click.stop="openSingleCreate(row)"
                >Opret fravær</button>
              </td>
              -->
            </tr>
            </tbody>
          </table>
        </template>

        <!-- Tabs: Udbakke / Sendt -->
        <template v-else>
          <Loader v-if="isLoading"></Loader>
          <table v-else class="do-table do-table--striped">
            <thead>
            <tr>
              <th class="td-created">OPRETTET</th>
              <th>NAVN</th>
              <th>ANSÆTTELSE</th>
              <th>FRAVÆRSTYPE</th>
              <th class="td-date">DATO</th>
              <th class="td-units">DAGE</th>
              <th class="td-units">TIMER</th>
              <th class="td-created-by">OPRETTET AF</th>
              <th class="td-status">STATUS</th>
              <th class="th-actions"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-if="displayedRows.length === 0">
              <td :colspan="10" class="italic">
                {{ viewTab === 'outbox' ? 'Udbakken er tom' : 'Ingen afsendte fraværsindberetninger' }}
              </td>
            </tr>
            <tr v-for="item in displayedRows" :key="item.vKey" class="fravaer-row">
              <td class="td-created">{{ item.vCreatedAt }}</td>
              <td>{{ item.vUserName }}</td>
              <td>{{ item.vEmploymentNumber }}</td>
              <td>{{ item.vAbsenceType }}</td>
              <td class="td-date">{{ item.vDato }}</td>
              <td class="td-units">{{ formatDecimal(item.dage) }}</td>
              <td class="td-units">{{ formatDecimal(item.timer) }}</td>
              <td class="td-created-by">{{ item.vCreatedBy }}</td>
              <td class="td-status">
                <span class="status-pill" :class="'status-pill--' + item.state">{{ stateLabel(item.state) }}</span>
              </td>
              <td class="td-actions">
                <button v-if="viewTab === 'outbox' && item.can_delete"
                        class="do-bttn do-bttn--small do-bttn--danger"
                        :disabled="deletingIds.includes(item.vKey)"
                        @click.stop="deleteRow(item)">
                  Slet
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </template>
      </div>
    </template>

    <!-- Manuel oprettelse af fravær er slået fra - fravær indberettes automatisk fra Vikardækning.
    <SdCreateFravaer
      v-if="createEmploymentIds.length > 0"
      :schoolId="schoolId"
      :employmentIds="createEmploymentIds"
      :subtitle="createSubtitle"
      :absenceCodes="absenceCodes"
      @created="onCreated"
      @close="closeCreate">
    </SdCreateFravaer>
    -->

    <SdAutoFravaer
      v-if="isAutoFravaerOpen"
      :schoolId="schoolId"
      :absenceCodes="absenceCodes"
      :startDate="autoStartDate"
      :endDate="autoEndDate"
      @startChanged="autoStartDate = $event"
      @endChanged="autoEndDate = $event"
      @created="onAutoFravaerCreated"
      @close="isAutoFravaerOpen = false">
    </SdAutoFravaer>

    <ModalConfirmation
      v-if="deleteCandidate"
      :showClose="false"
      :isSubmitButtonLoading="deletingIds.includes(deleteCandidate.vKey)"
      @close="deleteCandidate = null"
      @confirm="confirmDeleteRow">
      <div slot="custom_warning" class="delete-confirm__details">
        {{ deleteCandidate.vUserName }} &ndash; {{ deleteCandidate.vAbsenceType }} ({{ deleteCandidate.vDato }})
      </div>
    </ModalConfirmation>
  </div>
</template>

<script>
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'
import Loader from '../../common/Loader.vue'
import moment from 'moment'
import DoFiltersFilter from '../../DoFilters/DoFiltersFilter.vue'
// Manuel oprettelse af fravær er slået fra - fravær indberettes automatisk fra Vikardækning.
// import SdCreateFravaer from './SdCreateFravaer.vue'
import SdAutoFravaer from './SdAutoFravaer.vue'
import DoDatePickerRange from '../../datepicker/DoDatePickerRange.vue'
import Tabs from '../../common/Tabs.vue'
import ModalConfirmation from '../../Modals/ModalConfirmation.vue'

export default {
  name: 'SdFravaer',
  components: {Loader, DoFiltersFilter, SdAutoFravaer, DoDatePickerRange, Tabs, ModalConfirmation},
  data () {
    return {
      isNoConfiguration: false,
      isLoading: false,
      fravaer: [],
      sdFravaer: [],
      absenceCodes: [],
      selectedNames: [],
      selectedAbsenceTypes: [],
      viewTab: 'employments',
      selectedPersonNames: [],
      selectedUserTags: [],
      selectedEmploymentTitles: [],
      selectedOrganisations: [],
      selectedEmploymentIds: [],
      isBatchMode: false,
      createEmploymentIds: [],
      createSubtitle: '',
      isAutoFravaerOpen: false,
      autoStartDate: moment().startOf('month'),
      autoEndDate: moment().endOf('month'),
      deletingIds: [],
      deleteCandidate: null
    }
  },
  computed: {
    viewTabs () {
      return [
        {id: 'employments', title: 'Oprettelse'},
        {id: 'outbox', title: `Udbakke (${this.outboxCount})`},
        {id: 'sent', title: `Sendt til lønsystem (${this.sentCount})`}
      ]
    },
    outboxCount () {
      return this.fravaerSorted.filter(x => x.state !== 'sent').length
    },
    sentCount () {
      return this.fravaerSorted.filter(x => x.state === 'sent').length
    },
    schoolId () {
      return this.$store.getters.schoolId
    },
    isLoadingEmployments () {
      return this.$store.state.employments && this.$store.state.employments.isLoadingEmploymentsTypesForSchool
    },
    users () {
      return this.$store.getters.usersSortedOnName || []
    },
    usersById () {
      const map = {}
      const list = this.users
      for (let i = 0; i < list.length; i++) map[list[i].id] = list[i].name
      return map
    },
    employmentsInUserIdHash () {
      return this.$store.getters.employmentsInUserIdHash || {}
    },
    allRows () {
      const out = []
      for (let i = 0; i < this.users.length; i++) {
        const user = this.users[i]
        const emps = this.employmentsInUserIdHash[user.id] || []
        for (let j = 0; j < emps.length; j++) {
          const emp = emps[j]
          const empNumber = emp && emp.employment_number ? String(emp.employment_number) : ''
          if (empNumber.trim() === '') continue
          if (emp.source === 'opus') continue
          // Kun ansættelser med en stillingskategori kan vælges til fravær.
          if (emp.employment_template_id == null) continue
          const title = emp.source_title || emp.title || 'Ansættelse'
          out.push({
            id: user.id + '_' + emp.id,
            userId: user.id,
            userName: user.name,
            userTags: Array.isArray(user.user_tags) ? user.user_tags : [],
            employmentId: emp.id,
            employmentTitle: title,
            employmentNumber: empNumber,
            organisation: emp.organisation || '',
            employment: emp
          })
        }
      }
      return out.sort((a, b) => a.userName.localeCompare(b.userName, 'da-DK', {sensitivity: 'base'}))
    },
    personNames () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) if (this.allRows[i].userName) set[this.allRows[i].userName] = true
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    userTags () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) {
        const t = this.allRows[i].userTags || []
        for (let j = 0; j < t.length; j++) if (t[j]) set[t[j]] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    employmentTitles () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) if (this.allRows[i].employmentTitle) set[this.allRows[i].employmentTitle] = true
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    organisations () {
      const set = {}
      for (let i = 0; i < this.allRows.length; i++) if (this.allRows[i].organisation) set[this.allRows[i].organisation] = true
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da-DK'))
    },
    filteredRows () {
      return this.allRows.filter(row => {
        if (this.selectedPersonNames.length > 0 && this.selectedPersonNames.indexOf(row.userName) === -1) return false
        if (this.selectedUserTags.length > 0) {
          const match = row.userTags && this.selectedUserTags.some(t => row.userTags.indexOf(t) > -1)
          if (!match) return false
        }
        if (this.selectedEmploymentTitles.length > 0 && this.selectedEmploymentTitles.indexOf(row.employmentTitle) === -1) return false
        if (this.selectedOrganisations.length > 0 && this.selectedOrganisations.indexOf(row.organisation) === -1) return false
        return true
      })
    },
    isAllFilteredSelected () {
      if (this.filteredRows.length === 0) return false
      return this.filteredRows.every(row => this.selectedEmploymentIds.indexOf(row.employmentId) > -1)
    },
    groupedRows () {
      const groups = []
      const indexByUser = {}
      for (let i = 0; i < this.filteredRows.length; i++) {
        const row = this.filteredRows[i]
        if (indexByUser[row.userId] === undefined) {
          indexByUser[row.userId] = groups.length
          groups.push({userId: row.userId, rows: []})
        }
        groups[indexByUser[row.userId]].rows.push(row)
      }
      return groups
    },
    employmentsById () {
      const map = {}
      const list = (this.$store.state.employments && this.$store.state.employments.employments) || []
      for (let i = 0; i < list.length; i++) map[list[i].id] = list[i]
      return map
    },
    absenceCodesByLoenart () {
      const map = {}
      for (let i = 0; i < this.absenceCodes.length; i++) {
        const code = this.absenceCodes[i]
        if (code.loenart != null) map[String(parseInt(code.loenart, 10))] = code
      }
      return map
    },
    fravaerSorted () {
      const result = []
      for (let i = 0; i < this.fravaer.length; i++) {
        const item = this.fravaer[i]
        result.push(Object.assign({}, item, this.decorateFravaer(item), {
          vKey: 'ey_' + item.id,
          vDato: this.formatActivityDate(item),
          can_delete: item.can_delete != null ? item.can_delete : (item.state === 'draft')
        }))
      }
      // Fraværsindberetninger genereret fra Vikardækning (sd_fravaer) dækker en periode
      // (startdato-slutdato) i stedet for en enkelt aktivitetsdato.
      for (let i = 0; i < this.sdFravaer.length; i++) {
        const item = this.sdFravaer[i]
        result.push(Object.assign({}, item, this.decorateFravaer(item), {
          vKey: 'fv_' + item.id,
          isPeriod: true,
          vDato: this.formatPeriod(item),
          // dagdecimal kan have mange decimaler (timer/7,4) - vis afrundet til 2
          dage: item.dagdecimal != null ? Math.round(parseFloat(item.dagdecimal) * 100) / 100 : null,
          timer: null,
          can_delete: item['can_delete?'] != null ? item['can_delete?'] : (item.state === 'draft')
        }))
      }
      return result.sort((a, b) => {
        const av = a.created_at ? moment(a.created_at).valueOf() : 0
        const bv = b.created_at ? moment(b.created_at).valueOf() : 0
        return bv - av
      })
    },
    uniqueNames () {
      const set = {}
      for (let i = 0; i < this.fravaerSorted.length; i++) {
        const name = this.fravaerSorted[i].vUserName || ''
        if (name) set[name] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    uniqueAbsenceTypes () {
      const set = {}
      for (let i = 0; i < this.fravaerSorted.length; i++) {
        const t = this.fravaerSorted[i].vAbsenceType || ''
        if (t) set[t] = true
      }
      return Object.keys(set).sort((a, b) => a.localeCompare(b, 'da', {sensitivity: 'base'}))
    },
    displayedRows () {
      let rows = this.fravaerSorted.slice()
      rows = rows.filter(x => this.viewTab === 'sent' ? x.state === 'sent' : x.state !== 'sent')
      if (this.selectedNames.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedNames.length; i++) chosen[this.selectedNames[i]] = true
        rows = rows.filter(x => chosen[x.vUserName || ''])
      }
      if (this.selectedAbsenceTypes.length > 0) {
        const chosen = {}
        for (let i = 0; i < this.selectedAbsenceTypes.length; i++) chosen[this.selectedAbsenceTypes[i]] = true
        rows = rows.filter(x => chosen[x.vAbsenceType || ''])
      }
      return rows
    }
  },
  methods: {
    // Slår navnet op på den bruger (approved_by) der oprettede fraværsindberetningen.
    // Findes brugeren ikke i skolens brugerliste, vises selve id'et.
    createdByName (approvedBy) {
      if (approvedBy == null) return ''
      return this.usersById[approvedBy] || String(approvedBy)
    },
    // Fælles visningsfelter for både engangsydelse-fravær og periodebaseret sd_fravaer.
    decorateFravaer (item) {
      const employment = item.employment_id ? this.employmentsById[item.employment_id] : null
      const userId = (employment && employment.user_id) || item.user_id
      const vUserName = (userId && this.usersById[userId]) ||
        (item.tjenestenummer ? String(item.tjenestenummer) : 'Ukendt')
      const vEmploymentNumber = employment && employment.employment_number
        ? employment.employment_number + (employment.title ? ' - ' + employment.title : '')
        : (item.tjenestenummer ? String(item.tjenestenummer) : 'Ukendt')
      const code = item.loenart != null ? this.absenceCodesByLoenart[String(parseInt(item.loenart, 10))] : null
      const vAbsenceType = code ? code.loenart + ' - ' + code.title : (item.loenart != null ? String(item.loenart) : '')
      return {
        vUserName,
        vEmploymentNumber,
        vAbsenceType,
        vCreatedAt: item.created_at ? moment(item.created_at).format('lll') : '',
        vCreatedBy: this.createdByName(item.approved_by)
      }
    },
    // Perioden for et sd_fravaer. Igangværende fravær gemmes med slutdato 9999-12-31
    // og sendes til SD som '999999'.
    formatPeriod (item) {
      if (!item.startdato) return ''
      const start = moment(item.startdato).format('DD-MM-YYYY')
      if (!item.slutdato) return start
      const slut = moment(item.slutdato)
      if (slut.year() >= 9999) return start + ' - Åbenstående'
      if (item.startdato === item.slutdato) return start
      return start + ' - ' + slut.format('DD-MM-YYYY')
    },
    onSelectViewTab (tab) {
      this.viewTab = tab
      if (tab !== 'employments' && !this.isLoading) this.readFravaer()
    },
    togglePersonName (name) {
      const idx = this.selectedPersonNames.indexOf(name)
      if (idx > -1) this.selectedPersonNames.splice(idx, 1)
      else this.selectedPersonNames.push(name)
    },
    toggleUserTag (tag) {
      const idx = this.selectedUserTags.indexOf(tag)
      if (idx > -1) this.selectedUserTags.splice(idx, 1)
      else this.selectedUserTags.push(tag)
    },
    toggleEmploymentTitle (title) {
      const idx = this.selectedEmploymentTitles.indexOf(title)
      if (idx > -1) this.selectedEmploymentTitles.splice(idx, 1)
      else this.selectedEmploymentTitles.push(title)
    },
    toggleOrganisation (organisation) {
      const idx = this.selectedOrganisations.indexOf(organisation)
      if (idx > -1) this.selectedOrganisations.splice(idx, 1)
      else this.selectedOrganisations.push(organisation)
    },
    toggleName (name) {
      const idx = this.selectedNames.indexOf(name)
      if (idx > -1) this.selectedNames.splice(idx, 1)
      else this.selectedNames.push(name)
    },
    toggleAbsenceType (type) {
      const idx = this.selectedAbsenceTypes.indexOf(type)
      if (idx > -1) this.selectedAbsenceTypes.splice(idx, 1)
      else this.selectedAbsenceTypes.push(type)
    },
    isRowSelected (row) {
      return this.selectedEmploymentIds.indexOf(row.employmentId) > -1
    },
    toggleSelection (row) {
      const idx = this.selectedEmploymentIds.indexOf(row.employmentId)
      if (idx > -1) this.selectedEmploymentIds.splice(idx, 1)
      else this.selectedEmploymentIds.push(row.employmentId)
    },
    toggleAllSelected () {
      this.isBatchMode = true
      const visibleIds = this.filteredRows.map(row => row.employmentId)
      if (this.isAllFilteredSelected) {
        this.selectedEmploymentIds = this.selectedEmploymentIds.filter(id => visibleIds.indexOf(id) === -1)
      } else {
        const set = {}
        for (let i = 0; i < this.selectedEmploymentIds.length; i++) set[this.selectedEmploymentIds[i]] = true
        for (let i = 0; i < visibleIds.length; i++) set[visibleIds[i]] = true
        this.selectedEmploymentIds = Object.keys(set).map(id => parseInt(id, 10))
      }
    },
    cancelBatchMode () {
      this.isBatchMode = false
      this.selectedEmploymentIds = []
    },
    onRowClick (row) {
      if (this.isBatchMode) this.toggleSelection(row)
      else this.openSingleCreate(row)
    },
    onCheckboxClick (row) {
      if (!this.isBatchMode) this.isBatchMode = true
      this.toggleSelection(row)
    },
    openSingleCreate (row) {
      this.createEmploymentIds = [row.employmentId]
      this.createSubtitle = row.userName + ' – ' + row.employmentNumber + ' - ' + row.employmentTitle
    },
    openAutomaticIndberetning () {
      this.isAutoFravaerOpen = true
    },
    onAutoFravaerCreated (createdFravaer) {
      this.isAutoFravaerOpen = false
      if (createdFravaer && createdFravaer.length > 0) {
        this.viewTab = 'outbox'
        this.readFravaer()
      }
    },
    openBulkCreate () {
      if (this.selectedEmploymentIds.length === 0) return
      this.createEmploymentIds = this.selectedEmploymentIds.slice()
      this.createSubtitle = ''
    },
    closeCreate () {
      this.createEmploymentIds = []
      this.createSubtitle = ''
    },
    onCreated () {
      this.closeCreate()
      this.cancelBatchMode()
      this.viewTab = 'outbox'
      this.readFravaer()
    },
    // Åbner bekræftelses-modalen. Selve sletningen sker i confirmDeleteRow.
    deleteRow (item) {
      if (item.id == null || this.deletingIds.includes(item.vKey)) return
      this.deleteCandidate = item
    },
    // Engangsydelse-fravær og periodebaseret sd_fravaer slettes via hver deres endpoint.
    // deletingIds bruger vKey, da id'er fra de to tabeller kan kollidere.
    confirmDeleteRow () {
      const item = this.deleteCandidate
      if (!item || this.deletingIds.includes(item.vKey)) return
      this.deletingIds.push(item.vKey)
      const success = () => {
        this.deletingIds = this.deletingIds.filter(key => key !== item.vKey)
        if (item.isPeriod) {
          this.sdFravaer = this.sdFravaer.filter(r => r.id !== item.id)
        } else {
          this.fravaer = this.fravaer.filter(r => r.id !== item.id)
        }
        this.deleteCandidate = null
        eventbus.toast = {text: 'Fraværsindberetningen blev slettet fra udbakken', success: true, position: 'bottomLeft'}
      }
      const failure = error => {
        console.log('delete failure', error)
        this.deletingIds = this.deletingIds.filter(key => key !== item.vKey)
        this.deleteCandidate = null
        eventbus.toast = {text: 'Kunne ikke slette fraværsindberetningen', success: false, position: 'bottomLeft'}
      }
      if (item.isPeriod) {
        docendo.deleteSilkeborgDataFravaer(this.schoolId, item.id, success, failure)
      } else {
        docendo.deleteSilkeborgDataEngangsydelse(this.schoolId, item.id, 'fravaer', success, failure)
      }
    },
    formatActivityDate (item) {
      if (item.aar == null || item.maaned == null || item.dag == null) return ''
      const year = 2000 + parseInt(item.aar, 10)
      return moment({year: year, month: parseInt(item.maaned, 10) - 1, day: parseInt(item.dag, 10)}).format('DD-MM-YYYY')
    },
    formatDecimal (value) {
      if (value == null || value === '') return ''
      return String(value).replace('.', ',')
    },
    stateLabel (state) {
      switch (state) {
        case 'draft': return 'I udbakke'
        case 'sending': return 'Sender'
        case 'sent': return 'Sendt'
        default: return state || ''
      }
    },
    readFravaer () {
      this.isLoading = true
      let pending = 2
      const done = () => {
        pending--
        if (pending === 0) this.isLoading = false
      }
      docendo.listSilkeborgDataEngangsydelser(this.schoolId, 'fravaer', success => {
        if (success && success.status && success.status === 'error') {
          this.isNoConfiguration = true
        }
        if (Array.isArray(success)) {
          this.fravaer = success
        } else if (success && Array.isArray(success.data)) {
          this.fravaer = success.data
        } else {
          this.fravaer = []
        }
        done()
      }, failure => {
        console.log(failure)
        this.fravaer = []
        if (failure && failure.status && failure.status === 'error') {
          this.isNoConfiguration = true
        }
        done()
      })
      // Periodebaserede indberetninger genereret fra Vikardækning. Endpointet kræver et
      // datointerval; vi henter alt der overlapper ét år tilbage til ét år frem
      // (igangværende fravær overlapper altid, da slutdato er 9999-12-31).
      docendo.listSilkeborgDataFravaer(
        this.schoolId,
        moment().subtract(1, 'year').format('YYYY-MM-DD'),
        moment().add(1, 'year').format('YYYY-MM-DD'),
        success => {
          this.sdFravaer = Array.isArray(success) ? success : (success && Array.isArray(success.data) ? success.data : [])
          done()
        }, failure => {
          console.log(failure)
          this.sdFravaer = []
          done()
        })
    },
    readAbsenceCodes () {
      docendo.listSilkeborgDataAbsenceCodes(this.schoolId, success => {
        this.absenceCodes = Array.isArray(success) ? success : (success && Array.isArray(success.data) ? success.data : [])
      }, failure => {
        console.log(failure)
        this.absenceCodes = []
      })
    }
  },
  watch: {
    selectedEmploymentIds (ids) {
      if (!ids || ids.length === 0) this.isBatchMode = false
    }
  },
  mounted () {
    this.$store.dispatch('getUsersFromSchoolId', this.schoolId)
    this.$store.dispatch('readEmployments')
    this.readAbsenceCodes()
    this.readFravaer()
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.sd-fravaer {
  padding: 1rem;
}

.sd-fravaer__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.sd-fravaer__header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.sd-fravaer__header__right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-left: auto;
}

.sd-fravaer__toolbar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
}

.sd-fravaer__toolbar__wrap {
  display: inline-flex;
}

.sd-fravaer__count {
  font-style: italic;
  font-size: 11px;
  color: #6b7280;
}

.sd-fravaer__info {
  font-size: 12px;
  font-style: italic;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.sd-fravaer__info {
  font-size: 12px;
  font-style: italic;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

.do-table {
  width: 100%;
  border-collapse: collapse;
}

.do-table thead th {
  text-transform: uppercase;
  font-size: 10px;
  color: #8898aa;
  text-align: left;
  padding: 0.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.do-table tbody td {
  padding: 0.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.do-table tbody tr {
  transition: background-color 0.15s;
}

.do-table tbody tr:hover {
  background-color: aliceblue;
}

.do-table tbody.user-group:nth-of-type(2n) {
  background-color: #f9fafb;
}

.do-table tbody.user-group tr {
  cursor: pointer;
}

.do-table--striped tbody tr:nth-child(even) {
  background-color: #f9fafb;
}

.td-check {
  width: 26px;
}

.do-table thead th.td-check {
  border-right: 1px solid #e2e8f0;
}

.do-table tbody td.td-check {
  vertical-align: middle;
}

.do-table__toggle-all {
  color: var(--primary, #468ad7);
  cursor: pointer;
  user-select: none;
  text-transform: none;
  white-space: nowrap;
}

.do-table__toggle-all:hover {
  text-decoration: underline;
}

.do-table tbody tr.is-selected {
  background-color: #eaf3fb;
}

.do-checkbox {
  border: 1px solid #ccc;
  border-radius: .25rem;
  height: 1rem;
  position: relative;
  width: 1rem;
  cursor: pointer;
  display: inline-block;
  vertical-align: middle;
}

.do-checkbox.do-checkbox--selected {
  background: var(--primary);
  border-color: var(--primary);
}

.do-checkbox.do-checkbox--selected:after,
.do-checkbox.do-checkbox--selected:before {
  background: #fff;
}

.do-checkbox:after,
.do-checkbox:before {
  background: transparent;
  transform: rotate(35deg);
}

.do-checkbox:after {
  height: 8px;
  right: 3px;
  top: 1px;
  width: 1.5px;
}

.do-checkbox:after,
.do-checkbox:before {
  border-radius: 1rem;
  content: "";
  display: block;
  position: absolute;
}

.do-checkbox.do-checkbox--selected:before {
  bottom: 2.5px;
  height: 1.5px;
  right: 5px;
  width: 4px;
}

.td-organisation {
  opacity: 0.7;
  font-size: 11px;
}

.th-row-actions {
  width: 150px;
}

.td-row-actions {
  width: 150px;
  text-align: right;
  position: relative;
}

.do-bttn.do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.row-action-btn {
  opacity: 0;
  pointer-events: none;
  white-space: nowrap;
  transition: opacity 0.12s ease;
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);
}

.employment-row:hover .row-action-btn {
  opacity: 1;
  pointer-events: auto;
}

.td-date {
  width: 190px;
}

.td-units {
  width: 100px;
}

.td-units {
  text-align: right;
  width: 80px;
}

.do-table thead th.td-units {
  text-align: right;
}

.td-created {
  width: 150px;
}

.td-created-by {
  width: 140px;
}

.capitalize {
  text-transform: capitalize;
}

.td-status {
  width: 110px;
}

.th-actions, .td-actions {
  width: 90px;
  text-align: right;
}

.status-pill {
  display: inline-block;
  padding: 2px 8px;
  line-height: 1.5;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
}

.status-pill--draft {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-pill--sending {
  background-color: #fef9c3;
  color: #854d0e;
}

.status-pill--sent {
  background-color: #dcfce7;
  color: #166534;
}

.delete-confirm__details {
  padding: 0 1rem 0.5rem;
  font-size: 13px;
  color: #6b7280;
}
</style>



// WEBPACK FOOTER //
// SdFravaer.vue?1008cd2e